import { LightBulbIcon } from '@heroicons/react/24/outline'
import {
    Check,
    ChevronRight,
    ClipboardList,
    Compass,
    Eye,
    EyeOff,
    Heart,
    Puzzle,
    RefreshCw,
    Route,
    Sparkle,
    Sparkles,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import ROUTES from '@/ROUTES'

import { IUser } from '@/MODELS/user.model'

import { api } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'

import { SingleDataView } from '@/components/data-view/SingleDataView'
import Loading from '@/components/Loading'
import { SectionHeader } from '@/components/SectionHeader'

interface IBiteSizeStep {
    _id?: string
    task: string
    description: string
    relatedRoadmapStep?: string
    isChecked: boolean
    createdAt?: string
}

interface IFamilyHomework {
    _id: string
    title: string
    description: string | null
    dueDate: string | null
    assignedAt: string | null
}

function getTimeOfDay(): string {
    const hour = new Date().getHours()
    if (hour < 12) return 'morning'
    if (hour < 17) return 'afternoon'
    return 'evening'
}

export default function ParentDashboard() {
    const params = useParams()
    const { user } = useAuth()
    const navigate = useNavigate()
    const parentId = params.id || user?._id

    const [dailyTips, setDailyTips] = useState<string[]>([])
    const [biteSizeSteps, setBiteSizeSteps] = useState<IBiteSizeStep[]>([])
    const [loadingTips, setLoadingTips] = useState(false)
    const [loadingSteps, setLoadingSteps] = useState(false)
    const [refreshing, setRefreshing] = useState(false)
    const [showCompleted, setShowCompleted] = useState(false)
    const [aiUnavailable, setAiUnavailable] = useState(false)
    const uncheckedSteps = biteSizeSteps.filter((step) => !step.isChecked)
    const checkedSteps = biteSizeSteps.filter((step) => step.isChecked)
    const allCompleted = biteSizeSteps.length > 0 && uncheckedSteps.length === 0

    useEffect(() => {
        fetchDailyContent()
    }, [])

    const fetchDailyContent = async () => {
        setLoadingTips(true)
        setLoadingSteps(true)
        setAiUnavailable(false)
        const [tipsResult, stepsResult] = await Promise.allSettled([
            api<string[]>(ROUTES.app.dailyTips),
            api<IBiteSizeStep[]>(ROUTES.app.biteSizeSteps),
        ])
        if (tipsResult.status === 'fulfilled' && tipsResult.value?.data && (!tipsResult.value.status || tipsResult.value.status < 400)) {
            setDailyTips(tipsResult.value.data)
        } else {
            setAiUnavailable(true)
        }
        if (stepsResult.status === 'fulfilled' && stepsResult.value?.data && (!stepsResult.value.status || stepsResult.value.status < 400)) {
            setBiteSizeSteps(stepsResult.value.data)
        } else {
            setAiUnavailable(true)
        }
        setLoadingTips(false)
        setLoadingSteps(false)
    }
    const handleRefreshDailyContent = async () => {
        setRefreshing(true)
        try {
            const res = await api<{ tips: string[]; steps: IBiteSizeStep[] }>(ROUTES.app.regenerateDailyContent, {})
            if (res?.data && (!res.status || res.status < 400)) {
                setDailyTips(res.data.tips || [])
                setBiteSizeSteps(res.data.steps || [])
                setAiUnavailable(false)
            } else {
                setAiUnavailable(true)
            }
        } catch (error) {
            console.error('Failed to refresh daily content:', error)
            setAiUnavailable(true)
        } finally {
            setRefreshing(false)
        }
    }
    const handleCheckBiteSizeStep = async (stepId: string, checked: boolean) => {
        try {
            await api(ROUTES.app.checkBiteSizeStep, { stepId, checked })
            setBiteSizeSteps((prev) =>
                prev.map((step) => (step._id === stepId ? { ...step, isChecked: checked } : step))
            )
        } catch (error) {
            console.error('Failed to check step:', error)
        }
    }

    const isToday = (dateString?: string) => {
        if (!dateString) return true
        const date = new Date(dateString)
        const today = new Date()
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        )
    }

    const getVisibleSteps = () => {
        return biteSizeSteps.filter((step) => {
            if (isToday(step.createdAt)) return true
            if (step.isChecked) return false
            return true
        })
    }

    if (!user) {
        return (
            <div className='flex items-center justify-center min-h-screen bg-gradient-to-br from-brand-50 via-white to-secondary-50'>
                <Loading isLoading={true} mode='block' text='Loading your dashboard...' />
            </div>
        )
    }

    const tipConfig = [
        {
            id: 'child',
            label: 'For Your Child',
            icon: Puzzle,

            iconBg: 'bg-brand-100',
            iconColor: 'text-brand-600',
            fill: '#0fa0ce',
        },
        {
            id: 'wellness',
            label: 'For Your Wellness',
            icon: Heart,

            iconBg: 'bg-rose-100',
            iconColor: 'text-rose-500',
            fill: '#fb7185',
        },
        {
            id: 'journey',
            label: 'For Your Journey',
            icon: Route,

            iconBg: 'bg-amber-100',
            iconColor: 'text-amber-600',
        },
    ]

    return (
        <SingleDataView<IUser> endpoint={`${ROUTES.app.user}/${parentId}`}>
            {({ data, isLoading }) => {
                return (
                    <div className='flex-1'>
                        <SectionHeader
                            variant='hero'
                            title={`Good ${getTimeOfDay()}, ${user?.name?.split(' ')[0] || 'there'}!`}
                            subtitle={`Here's your personalized guidance for today. Small steps lead to big progress. `}
                            showDate
                        />

                        <div className='relative px-4 sm:px-6 lg:px-8 py-8 -mt-1'>
                            <div className='mx-auto space-y-8'>
                                {aiUnavailable && dailyTips.length === 0 && biteSizeSteps.length === 0 && (
                                    <div className='rounded-2xl bg-amber-50 border border-amber-200 p-5 text-center'>
                                        <p className='text-sm font-medium text-amber-800'>
                                            Personalized tips and quick wins are temporarily unavailable.
                                        </p>
                                        <p className='text-xs text-amber-600 mt-1'>
                                            Your roadmap, resources, and chat are still available. We're working to restore this feature.
                                        </p>
                                    </div>
                                )}
                                <ProviderHomeworkSection />
                                <section>
                                    <div className='flex items-center justify-between mb-5'>
                                        <div>
                                            <h2 className='flex items-center gap-1.5 text-lg font-semibold text-gray-900'>
                                                {' '}
                                                <LightBulbIcon className='w-5 h-5 shrink-0 text-brand-700' />
                                                Daily Tips
                                            </h2>
                                            <p className='text-sm text-gray-400 flex items-center gap-1.5'>
                                                <div className='w-4 h-4 text-amber-400/80 text-xs' />
                                                Personalized guidance just for you
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleRefreshDailyContent}
                                            disabled={refreshing}
                                            className='flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50'
                                            title='Get new tips'
                                        >
                                            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                                            <span className='hidden sm:inline'>Refresh</span>
                                        </button>
                                    </div>

                                    {loadingTips ? (
                                        <div className='flex items-center justify-center py-16'>
                                            <Loading isLoading={true} mode='block' />
                                        </div>
                                    ) : (
                                        <div className='grid gap-4'>
                                            {dailyTips.slice(0, 3).map((tip, index) => {
                                                const config = tipConfig[index]
                                                const Icon = config.icon

                                                return (
                                                    <div
                                                        key={index}
                                                        className={`relative flex items-start gap-4 p-5 rounded-2xl border border-gray-100 bg-white shadow-sm`}
                                                    >
                                                        <div
                                                            className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center border border-gray-100`}
                                                        >
                                                            <Icon
                                                                className={`w-5 h-5 ${config.iconColor}`}
                                                                strokeWidth={2}
                                                                {...(config.id === 'journey'
                                                                    ? {}
                                                                    : { fill: config.fill })}
                                                            />
                                                        </div>
                                                        <div className='flex-1 min-w-0 pt-0.5'>
                                                            <span
                                                                className={`text-xs font-semibold uppercase tracking-wide ${config.iconColor}`}
                                                            >
                                                                {config.label}
                                                            </span>
                                                            <p className='mt-1.5 text-[15px] text-gray-700 leading-relaxed'>
                                                                {tip}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}
                                </section>
                                <section>
                                    <div className='flex items-center justify-between mb-5'>
                                        <div>
                                            <h2 className='flex items-center gap-1.5 text-lg font-semibold text-gray-900'>
                                                Today's Quick Wins
                                            </h2>
                                            <p className='text-sm text-gray-400 flex items-center gap-1.5'>
                                                Small steps from your roadmap
                                            </p>
                                        </div>
                                        <div className='flex items-center gap-2'>
                                            {allCompleted && showCompleted && (
                                                <button
                                                    onClick={() => setShowCompleted(false)}
                                                    className='flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50'
                                                    title='Get new quick wins'
                                                >
                                                    <EyeOff className={`w-3.5 h-3.5 `} />
                                                    <span className='hidden sm:inline'> Hide</span>
                                                </button>
                                            )}

                                            <button
                                                onClick={() => navigate('/roadmap')}
                                                className='flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors'
                                            >
                                                <span className='hidden sm:inline'> View Roadmap </span>
                                                <ChevronRight className='w-4 h-4' />
                                            </button>
                                        </div>
                                    </div>
                                    {loadingSteps ? (
                                        <div className='flex items-center justify-center py-16'>
                                            <Loading isLoading={true} mode='block' />
                                        </div>
                                    ) : biteSizeSteps.length === 0 ? (
                                        <div className='rounded-2xl bg-gray-50 border border-gray-200 p-8 text-center'>
                                            <div className='w-12 h-12 mx-auto mb-3 rounded-xl bg-gray-100 flex items-center justify-center'>
                                                <Sparkle className='w-6 h-6 text-gray-400' />
                                            </div>
                                            <h3 className='text-base font-semibold text-gray-900 mb-1'>
                                                No quick wins available
                                            </h3>
                                            <p className='text-sm text-gray-500'>
                                                Complete some roadmap tasks to get personalized quick wins.
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            {allCompleted && !showCompleted && (
                                                <div className='rounded-2xl bg-white border border-gray-100 p-6 text-center mb-4'>
                                                    <div className='w-12 h-12 mx-auto mb-3 rounded-xl bg-secondary-100 flex items-center justify-center'>
                                                        <Check className='w-6 h-6 text-secondary-600' />
                                                    </div>
                                                    <h3 className='text-base font-semibold text-gray-900 mb-1'>
                                                        You're all caught up!
                                                    </h3>
                                                    <p className='text-sm text-gray-500 mb-4'>
                                                        Great job completing today's quick wins.
                                                    </p>
                                                    <button
                                                        onClick={() => setShowCompleted(true)}
                                                        className='inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-brand-700 hover:text-secondary-600 rounded-lg transition-colors'
                                                    >
                                                        Show completed tasks
                                                        <Eye className='w-4 h-4' />
                                                    </button>
                                                </div>
                                            )}

                                            {(!allCompleted || showCompleted) && (
                                                <>
                                                    <div className='space-y-3 px-2'>
                                                        {[...uncheckedSteps, ...checkedSteps]
                                                            .slice(0, 3)
                                                            .map((step, index) => (
                                                                <div
                                                                    key={step._id || index}
                                                                    className={`relative rounded-r-xl bg-white border transition-colors shadow-sm ${
                                                                        step.isChecked
                                                                            ? 'bg-gray-50/50 border-gray-100'
                                                                            : 'border-gray-100 hover:border-gray-200'
                                                                    }`}
                                                                >
                                                                    <div
                                                                        className={`absolute left-0 top-0 bottom-0 w-1 rounded-full ${
                                                                            step.isChecked
                                                                                ? 'bg-secondary-400'
                                                                                : 'bg-brand-500'
                                                                        }`}
                                                                    />

                                                                    <div className='p-4'>
                                                                        <div className='flex items-start gap-3'>
                                                                            <button
                                                                                onClick={() =>
                                                                                    handleCheckBiteSizeStep(
                                                                                        step._id!,
                                                                                        !step.isChecked
                                                                                    )
                                                                                }
                                                                                className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-colors ${
                                                                                    step.isChecked
                                                                                        ? 'bg-secondary-500 border-secondary-500'
                                                                                        : 'border-gray-300 hover:border-brand-400'
                                                                                }`}
                                                                                title={
                                                                                    step.isChecked
                                                                                        ? 'Mark as incomplete'
                                                                                        : 'Mark as complete'
                                                                                }
                                                                            >
                                                                                {step.isChecked && (
                                                                                    <Check
                                                                                        className='w-3 h-3 text-white'
                                                                                        strokeWidth={3}
                                                                                    />
                                                                                )}
                                                                            </button>

                                                                            <div className='flex-1 min-w-0'>
                                                                                <h4
                                                                                    className={`text-sm font-medium ${
                                                                                        step.isChecked
                                                                                            ? 'text-gray-400 line-through'
                                                                                            : 'text-gray-900'
                                                                                    }`}
                                                                                >
                                                                                    {step.task}
                                                                                </h4>
                                                                                <QuickWinDescription description={step.description} isChecked={step.isChecked} />

                                                                                {step.relatedRoadmapStep && (
                                                                                    <span
                                                                                        className={`inline-flex items-center gap-1 mt-2 text-xs ${
                                                                                            step.isChecked
                                                                                                ? 'text-gray-400'
                                                                                                : 'text-gray-500'
                                                                                        }`}
                                                                                    >
                                                                                        <Route className='w-3 h-3' />
                                                                                        {step.relatedRoadmapStep}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </section>
                            </div>
                        </div>
                    </div>
                )
            }}
        </SingleDataView>
    )
}

// "From your provider" — homework assigned by the family's sponsoring
// organization. Renders nothing for B2C users or when there is no open
// homework, so ordinary dashboards are unchanged.
function ProviderHomeworkSection() {
    const [items, setItems] = useState<IFamilyHomework[]>([])
    const [orgName, setOrgName] = useState<string | null>(null)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        let cancelled = false
        async function load() {
            const res = await api<{ orgName: string | null; items: IFamilyHomework[] }>(ROUTES.app.homework)
            if (cancelled) return
            if (res?.data && (!res.status || res.status < 400)) {
                setItems(res.data.items ?? [])
                setOrgName(res.data.orgName ?? null)
            }
            setLoaded(true)
        }
        load()
        return () => {
            cancelled = true
        }
    }, [])

    // Not loaded yet, or not a sponsored family (B2C / ended sponsorship, where the
    // endpoint omits orgName): render nothing so ordinary dashboards are unchanged.
    // Sponsored families with no open homework still see the section with an empty
    // state, so the space where activities appear is discoverable.
    if (!loaded || !orgName) return null

    return (
        <section>
            <div className='mb-5'>
                <h2 className='flex items-center gap-1.5 text-lg font-semibold text-gray-900'>
                    <ClipboardList className='w-5 h-5 shrink-0 text-brand-700' />
                    From your provider
                </h2>
                <p className='text-sm text-gray-400'>Sent by {orgName}</p>
            </div>
            {items.length === 0 ? (
                <div className='rounded-xl bg-white border border-gray-100 shadow-sm p-4 text-sm text-gray-500'>
                    You're all caught up. New activities from your provider will appear here.
                </div>
            ) : (
                <div className='space-y-3'>
                    {items.map((item) => (
                        <HomeworkCard
                            key={item._id}
                            item={item}
                            onCompleted={(id) => setItems((prev) => prev.filter((i) => i._id !== id))}
                        />
                    ))}
                </div>
            )}
        </section>
    )
}

function HomeworkCard({ item, onCompleted }: { item: IFamilyHomework; onCompleted: (id: string) => void }) {
    const [completing, setCompleting] = useState(false)
    const [note, setNote] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const due = item.dueDate ? new Date(item.dueDate) : null
    const dueLabel =
        due && !isNaN(due.valueOf())
            ? due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : null
    const overdue = due ? due.valueOf() < Date.now() : false

    async function handleComplete() {
        setSubmitting(true)
        setError(null)
        const trimmed = note.trim()
        const res = await api(
            ROUTES.app.homeworkComplete.replace(':id', item._id),
            trimmed ? { note: trimmed } : {}
        )
        setSubmitting(false)
        if (res.status && res.status < 400) {
            onCompleted(item._id)
        } else {
            setError(res.error || 'Something went wrong. Please try again.')
        }
    }

    return (
        <div className='relative rounded-r-xl bg-white border border-gray-100 shadow-sm'>
            <div className='absolute left-0 top-0 bottom-0 w-1 rounded-full bg-brand-500' />
            <div className='p-4'>
                <div className='flex items-start gap-3'>
                    <button
                        onClick={() => setCompleting(true)}
                        aria-label={`Mark "${item.title}" complete`}
                        className={`mt-0.5 flex-shrink-0 flex items-center justify-center w-5 h-5 rounded border-2 transition-colors ${
                            completing
                                ? 'border-brand-600 bg-brand-600 text-white'
                                : 'border-gray-300 hover:border-brand-500 hover:bg-brand-50'
                        }`}
                    >
                        {completing && <Check className='w-3.5 h-3.5' strokeWidth={3} />}
                    </button>
                    <div className='flex-1 min-w-0'>
                        <h4 className='text-sm font-medium text-gray-900'>{item.title}</h4>
                        {item.description && (
                            <p className='text-sm text-gray-500 leading-relaxed mt-1 whitespace-pre-wrap'>
                                {item.description}
                            </p>
                        )}
                        {dueLabel && (
                            <span
                                className={`inline-block mt-2 text-xs ${overdue ? 'font-medium text-red-600' : 'text-gray-500'}`}
                            >
                                Due {dueLabel}
                            </span>
                        )}
                    </div>
                </div>

                {completing && (
                    <div className='mt-3 pt-3 border-t border-gray-100'>
                        <textarea
                            rows={2}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            maxLength={1000}
                            placeholder='Add a note for your provider (optional)'
                            className='w-full px-3 py-2 border border-gray-200 rounded-lg text-sm'
                        />
                        {error && <p className='mt-1 text-xs text-red-600'>{error}</p>}
                        <div className='mt-2 flex items-center gap-2'>
                            <button
                                onClick={handleComplete}
                                disabled={submitting}
                                className='inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50'
                            >
                                <Check className='w-3.5 h-3.5' strokeWidth={3} />
                                {submitting ? 'Saving…' : 'Mark complete'}
                            </button>
                            <button
                                onClick={() => {
                                    setCompleting(false)
                                    setError(null)
                                }}
                                className='px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100 rounded-lg'
                            >
                                Not yet
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

function QuickWinDescription({ description, isChecked }: { description: string; isChecked: boolean }) {
    const [show, setShow] = useState(false)
    if (!description) return null
    return show ? (
        <div>
            <p className={`text-sm leading-relaxed mt-1 ${isChecked ? 'text-gray-400' : 'text-gray-500'}`}>
                {description}
            </p>
            <button onClick={() => setShow(false)} className='block text-xs text-brand-500 hover:text-brand-600 mt-0.5'>
                Show less
            </button>
        </div>
    ) : (
        <button onClick={() => setShow(true)} className='block text-xs text-brand-500 hover:text-brand-600 mt-1'>
            Show more
        </button>
    )
}
